import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Form, Input, Select } from "antd";
import { UserOutlined, MoreOutlined, MessageOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { FetchPlaceholderFields } from "../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../store/filters";
import { getTemplateComponents, AddTemplate } from "../../../assets/api/templateService";
import { useNavigate } from "react-router-dom";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import PanelCard from "../../DashboardPage/components/PanelCard";
import "./SMSCreateTemplate.scss";

const { TextArea } = Input;
const { Option } = Select;

const BODY_LIMIT = 1024;

const SMSCreateTemplate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [bodyText, setBodyText] = useState("");
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const { filter_list } = useSelector((state) => state.filters) || {};

  useEffect(() => {
    dispatch(fetchUserFilters());
  }, [dispatch]);

  const dynamicPortfoliosOptions = useMemo(() => {
    const portfoliosData = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedBodyPlaceholder, setSelectedBodyPlaceholder] = useState(null);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  useEffect(() => {
    if (token) {
      setLoadingComponents(true);
      getTemplateComponents(token)
        .then((res) => {
          if (res?.data?.data) {
            const { languages, categories } = res?.data?.data;
            if (languages) {
              setLanguageOptions(languages.map(lang => ({ label: lang.label, value: lang.code })));
            }
            if (categories) {
              setCategoryOptions(categories.map(cat => ({ label: cat.label, value: cat.value })));
            }
          }
        })
        .catch((err) => console.error("Error fetching template components:", err))
        .finally(() => setLoadingComponents(false));
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      setLoadingFields(true);
      FetchPlaceholderFields(token)
        .then((res) => {
          let dataArray = [];
          if (res?.data?.data?.field_names && Array.isArray(res.data.data.field_names)) {
            dataArray = res.data.data.field_names;
          } else if (res?.data?.field_names && Array.isArray(res.data.field_names)) {
            dataArray = res.data.field_names;
          } else if (Array.isArray(res?.data)) {
            dataArray = res.data;
          } else if (Array.isArray(res?.data?.data)) {
            dataArray = res.data.data;
          }

          const formatLabel = (str) => {
            if (!str) return "";
            return str
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ");
          };

          const options = dataArray.map((item) => {
            const strVal = typeof item === "string" ? item : item?.name || item?.key || String(item);
            return { value: strVal, label: formatLabel(strVal) };
          });

          setPlaceholderFields(options);
        })
        .catch((err) => console.error("Error fetching placeholders:", err))
        .finally(() => setLoadingFields(false));
    }
  }, [token]);

  const handleBodyPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentBody = form.getFieldValue("body") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentBody.length + placeholderText.length > BODY_LIMIT) {
      CustomNotification.show("Placeholder cannot be added as it exceeds the 1024 characters limit.", "error");
      setSelectedBodyPlaceholder(null);
      return;
    }
    const newBody = currentBody + placeholderText;
    form.setFieldsValue({ body: newBody });
    setBodyText(newBody);
    setSelectedBodyPlaceholder(null);
  }, [form]);

  const onFinish = useCallback(async (values) => {
    try {
      setSubmitting(true);

      const components = [];

      if (values.body) {
        components.push({
          type: "BODY",
          text: values.body,
        });
      }

      const payload = {
        portfolio_id: Number(values.portfolio),
        channel: "sms",
        name: values.name,
        sender_id: values.sender_id,
        dlt_template_id: values.dlt_template_id,
        language: values.language,
        category: values.category,
        components,
      };

      const res = await AddTemplate(token, payload);
      if (res.status === 201 || res.status === 200) {
        CustomNotification.show("Template created successfully", "success");
        navigate("/communication/sms/template");
      }
    } catch (err) {
      CustomNotification.show(err || "Failed to create template", "error");
    } finally {
      setSubmitting(false);
    }
  }, [token, navigate]);

  const toggleBodyExpanded = useCallback(() => {
    setIsBodyExpanded((prev) => !prev);
  }, []);

  const handleBackClick = useCallback(() => {
    navigate("/communication/sms/template");
  }, [navigate]);

  const used = bodyText.length;
  /* The counter turns as the limit approaches rather than only reporting it
     once a placeholder is refused. */
  const countState =
    used >= BODY_LIMIT ? "is-full" : used > BODY_LIMIT * 0.9 ? "is-near" : "";

  return (
    <div className="sms-create-template-container">
      <div className="sms-left-section">
        <PanelCard
          title="Create SMS Template"
          icon={<MessageOutlined />}
          accent="brand"
          className="smsCreateTemplateMain"
        >
          <Form form={form} layout="vertical" onFinish={onFinish}>
            {/* A real grid, not `flex: 1 1 200px` wrapping - the fields used
                to reflow into ragged rows whose columns did not line up. */}
            <div className="smsCreateTemplateFormWrapper">
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Please enter template name" }]}
              >
                <Input placeholder="Enter Name" />
              </Form.Item>
              <Form.Item
                label="Sender Id"
                name="sender_id"
                rules={[{ required: true, message: "Please enter sender id" }]}
              >
                <Input placeholder="Enter Sender Id" />
              </Form.Item>
              <Form.Item
                label="Dlt Template Id"
                name="dlt_template_id"
                rules={[{ required: true, message: "Please enter Dlt template id" }]}
              >
                <Input placeholder="Enter Dlt Template Id" />
              </Form.Item>
              <Form.Item
                label="Language"
                name="language"
                rules={[{ required: true, message: "Please select language" }]}
              >
                <Select placeholder="Select Language" options={languageOptions} loading={loadingComponents} allowClear />
              </Form.Item>
              <Form.Item
                label="Portfolio"
                name="portfolio"
                rules={[{ required: true, message: "Please select portfolio" }]}
              >
                <Select placeholder="Select Portfolio" options={dynamicPortfoliosOptions} allowClear />
              </Form.Item>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select placeholder="Select Category" options={categoryOptions} loading={loadingComponents} allowClear />
              </Form.Item>
              <Form.Item
                label="Template Type"
                name="templateType"
                initialValue="default"
                rules={[{ required: true, message: "Please select template type" }]}
              >
                <Select placeholder="Default">
                  <Option value="default">Default</Option>
                </Select>
              </Form.Item>
            </div>

            {/* The message and the placeholder that writes into it sit in one
                block. The label used to be a hand-built .ant-form-item-label
                div with the picker floating at the far end of the row. */}
            <div className="sms-custom-body-section">
              <div className="smsBodyHead">
                {/* antd puts the required mark before the label, so this
                    hand-built one matches the seven fields above it. */}
                <span className="smsBodyLabel">
                  <i aria-hidden="true">*</i> Body
                </span>

                <Select
                  className="smsPlaceholder"
                  showSearch
                  placeholder="Insert placeholder"
                  options={placeholderFields}
                  loading={loadingFields}
                  value={selectedBodyPlaceholder}
                  onChange={handleBodyPlaceholderSelect}
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              <Form.Item
                name="body"
                rules={[{ required: true, message: "Please enter body text" }]}
              >
                <TextArea
                  rows={8}
                  maxLength={BODY_LIMIT}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Enter the text for the template in the appropriate language"
                />
              </Form.Item>

              <div className={`char-count ${countState}`} aria-live="polite">
                <span>{used}</span> / {BODY_LIMIT}
              </div>
            </div>

            <div className="smsCreateTemplateSubmitAction">
              <NormalButton
                type="default"
                onClick={handleBackClick}
                className="smsCreateTemplateCancelBtn"
              >
                Back
              </NormalButton>
              <NormalButton
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="smsCreateTemplateSubmitBtn"
              >
                Create
              </NormalButton>
            </div>
          </Form>
        </PanelCard>
      </div>

      {/* Right Section - SMS Preview */}
      <div className="sms-right-section">
        <div className="sms-preview-label">Preview</div>

        <div className="sms-mockup">
          <span className="sms-mockup__notch" aria-hidden="true" />

          <div className="sms-header">
            <div className="sms-header-left">
              <div className="sms-avatar">
                <UserOutlined />
              </div>
              <span className="sms-name">Text Message</span>
            </div>
            <MoreOutlined />
          </div>

          <div className="sms-body">
            <div className="sms-date">
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>

            {bodyText ? (
              <div className="sms-message-bubble">
                <div className="sms-message-body">
                  {bodyText.length > 250 ? (
                    <>
                      {isBodyExpanded ? bodyText : `${bodyText.substring(0, 250)}... `}
                      <button
                        type="button"
                        className="sms-message-more"
                        onClick={toggleBodyExpanded}
                      >
                        {isBodyExpanded ? 'show less' : 'show more'}
                      </button>
                    </>
                  ) : (
                    bodyText
                  )}
                </div>
              </div>
            ) : (
              /* the preview used to be a blank white screen until the first
                 character was typed */
              <div className="sms-empty">
                <MessageOutlined />
                <span>Your message will appear here as you type.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(SMSCreateTemplate);
